// src/components/avatars/Avatar.js

import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { sanitizeURL } from '../../utils/sanitize';
import { resolveAvatarSize } from './size';
import styles from './Avatar.module.css';

export const AVATAR_VARIANTS = {
  USER: 'user',
  GROUP: 'group'
};

const hashStringToHue = (value) => {
  if (!value) {
    return 210;
  }

  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = value.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % 360;
};

const stringToColor = (seed) => {
  const value = seed ?? '';
  return `hsl(${hashStringToHue(value)}, 70%, 60%)`;
};

const normaliseWhitespace = (value) => value?.trim().replace(/\s+/g, ' ') || '';

const defaultInitialsUser = (name) => {
  const normalised = normaliseWhitespace(name);
  if (!normalised) return '?';

  const parts = normalised.split(' ');
  if (parts.length === 1) {
    return parts[0].slice(0, 1).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const defaultInitialsGroup = (name) => {
  const normalised = normaliseWhitespace(name);
  if (!normalised) return '?';

  const parts = normalised.split(' ');
  if (parts.length > 1) {
    return parts
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }

  return normalised.slice(0, 2).toUpperCase();
};

const defaultInitialsByVariant = {
  [AVATAR_VARIANTS.USER]: defaultInitialsUser,
  [AVATAR_VARIANTS.GROUP]: defaultInitialsGroup
};

export const getInitials = (name, variant = AVATAR_VARIANTS.USER) => {
  const strategy = defaultInitialsByVariant[variant] ?? defaultInitialsUser;
  return strategy(name);
};

const Avatar = forwardRef(function Avatar(
  {
    as: As = 'span',
    name,
    variant = AVATAR_VARIANTS.USER,
    renderInitials,
    colorSeed,
    fallbackColor,
    textColor,
    size,
    shape = 'circle',
    className = '',
    style,
    src,
    srcSet,
    sizes,
    alt,
    allowUnsafeSrc = false,
    loading = 'lazy',
    onError,
    onLoad,
    ...rest
  },
  ref
) {
  const sanitizedSrc = useMemo(() => {
    if (!src) return null;
    if (allowUnsafeSrc) return src;
    return sanitizeURL(src);
  }, [src, allowUnsafeSrc]);

  const hasSrc = Boolean(sanitizedSrc);
  const [status, setStatus] = useState(hasSrc ? 'loading' : 'idle');
  const requestIdRef = useRef(0);
  const imgRef = useRef(null);

  useEffect(() => {
    requestIdRef.current += 1;
    const currentRequest = requestIdRef.current;
    const img = imgRef.current;

    if (!hasSrc || !img) {
      setStatus('idle');
      if (img) {
        img.removeAttribute('data-avatar-request-id');
      }
      return undefined;
    }

    setStatus('loading');
    img.dataset.avatarRequestId = String(currentRequest);

    if (!img.complete) {
      return undefined;
    }

    let cancelled = false;
    const settleFromCache = async () => {
      if (img.naturalWidth === 0) {
        if (!cancelled && requestIdRef.current === currentRequest) {
          setStatus('error');
        }
        return;
      }

      try {
        if (typeof img.decode === 'function') {
          await img.decode();
        }
      } catch (error) {
        // decode may reject in some browsers (e.g., Safari); ignore and continue.
      }

      if (cancelled || requestIdRef.current !== currentRequest) {
        return;
      }

      setStatus('loaded');
    };

    void settleFromCache();

    return () => {
      cancelled = true;
    };
  }, [hasSrc, sanitizedSrc, srcSet, sizes]);

  const sizeResolution = useMemo(() => resolveAvatarSize(size), [size]);
  const initialsStrategy = renderInitials || defaultInitialsByVariant[variant] || defaultInitialsUser;
  const initials = useMemo(() => initialsStrategy(name), [initialsStrategy, name]);
  const label = alt ?? (name ? `${name} avatar` : 'Avatar');
  const isDecorative = alt === '';
  const isLoaded = status === 'loaded';
  const showInitials = !hasSrc || status === 'error';

  const handleImageError = useCallback(
    (event) => {
      const target = event.currentTarget;
      const eventRequestId = Number(target.dataset.avatarRequestId || '0');
      if (eventRequestId !== requestIdRef.current) {
        return;
      }

      setStatus('error');
      if (onError) {
        onError(event);
      }
    },
    [onError]
  );

  const handleImageLoad = useCallback(
    (event) => {
      const target = event.currentTarget;
      const eventRequestId = Number(target.dataset.avatarRequestId || '0');

      const promote = async () => {
        if (eventRequestId !== requestIdRef.current) {
          return;
        }

        try {
          if (typeof target.decode === 'function') {
            await target.decode();
          }
        } catch (error) {
          // Some browsers throw on decode (e.g., if already decoded); ignore.
        }

        if (eventRequestId !== requestIdRef.current) {
          return;
        }

        setStatus('loaded');
        if (onLoad) {
          onLoad(event);
        }
      };

      void promote();
    },
    [onLoad]
  );

  const computedBackground = useMemo(() => {
    if (fallbackColor) return fallbackColor;
    const seed = colorSeed ?? name ?? '';
    return stringToColor(seed);
  }, [fallbackColor, colorSeed, name]);

  const computedForeground = textColor ?? 'var(--background-elevated)';

  const styleVars = {
    '--avatar-bg': computedBackground,
    '--avatar-fg': computedForeground
  };

  if (sizeResolution.kind === 'custom' && sizeResolution.value) {
    styleVars['--avatar-size'] = sizeResolution.value;
  }

  const mergedStyle = style ? { ...styleVars, ...style } : styleVars;
  const hasExplicitSize = size != null;
  const dataSize = hasExplicitSize
    ? sizeResolution.kind === 'token'
      ? sizeResolution.token
      : sizeResolution.kind === 'custom'
        ? 'custom'
        : undefined
    : undefined;

  const fallbackA11yProps = isDecorative
    ? { 'aria-hidden': true }
    : isLoaded
      ? { 'aria-hidden': true }
      : { role: 'img', 'aria-label': label };

  return (
    <As
      ref={ref}
      className={`${styles.avatar} ${className}`.trim()}
      data-variant={variant}
      data-shape={shape}
      data-size={dataSize}
      data-fluid={sizeResolution.kind === 'fluid' ? '' : undefined}
      data-state={status}
      style={mergedStyle}
      title={isDecorative ? undefined : (name || undefined)}
      {...rest}
    >
      <img
        ref={imgRef}
        src={sanitizedSrc ?? undefined}
        srcSet={srcSet}
        sizes={sizes}
        alt={label}
        className={styles.image}
        loading={loading}
        decoding="async"
        fetchpriority={loading === 'eager' ? 'high' : undefined}
        draggable={false}
        aria-hidden={isDecorative || !isLoaded ? true : undefined}
        onError={handleImageError}
        onLoad={handleImageLoad}
      />
      <span className={styles.fallback} {...fallbackA11yProps}>
        {showInitials ? initials : null}
      </span>
    </As>
  );
});

Avatar.displayName = 'Avatar';

Avatar.propTypes = {
  as: PropTypes.elementType,
  name: PropTypes.string,
  variant: PropTypes.oneOf(Object.values(AVATAR_VARIANTS)),
  renderInitials: PropTypes.func,
  colorSeed: PropTypes.string,
  fallbackColor: PropTypes.string,
  textColor: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.oneOf(['fluid', 'xs', 'sm', 'md', 'lg', 'xl']), PropTypes.number, PropTypes.string]),
  shape: PropTypes.oneOf(['circle', 'rounded', 'squircle', 'square']),
  className: PropTypes.string,
  style: PropTypes.object,
  src: PropTypes.string,
  srcSet: PropTypes.string,
  sizes: PropTypes.string,
  alt: PropTypes.string,
  allowUnsafeSrc: PropTypes.bool,
  loading: PropTypes.oneOf(['lazy', 'eager']),
  onError: PropTypes.func,
  onLoad: PropTypes.func
};

export default Avatar;
